export default (items, search, options, containerRef, max, withTitles, fillInputWithOptions) => ({
    isOpen: false,
    selected: null,
    items,
    search,
    options,
    containerRef,
    max,
    withTitles,
    fillInputWithOptions,
    shortcuts: {
        ['@click.outside']() {
            this.isOpen = false;
        },
        ['@keyup.enter'](event) {
            event.stopPropagation();

            if (!this.isOpen) {
                this.isOpen = true
            }

            this.pickOption(this.selected)
        },
        ['@keyup.esc'](event) {
            event.stopPropagation();
            this.isOpen = false
        },
        ['@keyup.up']() {
            this.setSelectedOption('up');
        },
        ['@keyup.down']() {
            this.setSelectedOption('down');
        },
    },
    init() {

        this.selectOption(this.filteredOnlyOptions[0]?.id)

        this.setOptionsSizes();

        this.$watch('isOpen', value => {
            if (value) {
                this.$nextTick(() => {
                    this.$refs.searchInput.focus({
                        preventScroll: true,
                    });
                    this.setOptionsSizes();
                });

                return;
            }

            this.clearState();
        });
        this.$watch('items', () => this.$nextTick(() => this.setOptionsSizes()));
        this.$watch('search', () => this.$nextTick(() => this.setOptionsSizes()));

        this.$wire.$watch('options', () => this.$wire.call('updateRows'));

        this.$watch('options', () => {
            this.selectOption(this.filteredOnlyOptions[0]?.id)

            this.items = this.selectedOptions.map(option => option.id);
        });

        this.$wire.$watch('search', () => this.selectOption(this.filteredOnlyOptions[0]?.id));

        this.repositionOptions = () => {
            if (this.isOpen) {
                this.setOptionsSizes();
            }
        };

        window.addEventListener('scroll', this.repositionOptions, true);
        window.addEventListener('resize', this.repositionOptions);
    },
    destroy() {
        window.removeEventListener('scroll', this.repositionOptions, true);
        window.removeEventListener('resize', this.repositionOptions);
    },
    get filteredOnlyOptions() {
        return this.filterOptions(this.onlyOptions);
    },
    get onlyOptions() {
        if (this.withTitles) {
            return Object.values(this.options).flat().sort((a, b) => a.index - b.index);
        } else {
            return this.options;
        }
    },
    get filteredOptions() {
        if (this.withTitles) {
            return Object.keys(this.options).reduce((items, option) => {
                const filterItems = this.filterOptions(this.options[option]);

                if (filterItems.length) {
                    items[option] = filterItems;
                }

                return items;
            }, []);
        }

        return this.filterOptions(this.options);
    },
    get selectedOptions() {
        const items = this.items || [];

        return this.onlyOptions.filter((option) => items.includes(option.id));
    },
    filterOptions(options) {
        return options.filter((option) =>
            !this.search || option.label.toLowerCase().includes(this.search.toLowerCase())
        );
    },
    get isCheckedAll() {
        return this.items?.length === this.onlyOptions.length;
    },
    selectAll() {
        if (this.isCheckedAll) {
            this.items = [];
            return;
        }

        if (this.max && this.onlyOptions.length > this.max) {
            this.items = this.items || [];

            const selectedItems = new Set(this.items);
            const newItems = this.onlyOptions
                .filter(option => !selectedItems.has(option.id))
                .slice(0, this.max - this.items.length)
                .map(option => option.id);

            this.items = [...this.items, ...newItems];

            return;
        }

        this.items = this.onlyOptions.map(option => option.id);
    },
    clearState() {
        this.search = '';
        this.selectOption(this.filteredOnlyOptions[0]?.id);
    },
    canFlipToTop() {
        const inputTopPosition = this.$refs.input.getBoundingClientRect().top + 6;
        const containerTopPosition = this.containerRef
            ? this.$refs[this.containerRef].getBoundingClientRect().top
            : 0;

        const optionsHeight = this.$refs.options.clientHeight;

        return (inputTopPosition - containerTopPosition) > optionsHeight;
    },
    getFlipDirection() {
        const optionsHeight = this.$refs.options.clientHeight;
        const inputPosition = this.$refs.input.getBoundingClientRect().bottom + 6;

        const containerHeight = this.containerRef
            ? this.$refs[this.containerRef].clientHeight
            : document.documentElement.clientHeight;

        const optionsContainerFitsBottom = (containerHeight - inputPosition) >= optionsHeight;

        return optionsContainerFitsBottom || !this.canFlipToTop() ? 'bottom' :  'top';
    },
    setOptionsSizes() {
        const options = this.$refs.options;
        const input = this.$refs.input;

        if (!options || !input) {
            return;
        }

        const container = this.containerRef ? this.$refs[this.containerRef] : null;
        const containerRect = container ? container.getBoundingClientRect() : null;
        const offsetTop = containerRect ? containerRect.top : 0;
        const offsetLeft = containerRect ? containerRect.left : 0;

        const inputRect = input.getBoundingClientRect();
        const flipDropdownTo = this.getFlipDirection();

        options.style.width = `${inputRect.width}px`;
        options.style.left = `${inputRect.left - offsetLeft}px`;
        options.style.top = flipDropdownTo === 'bottom'
            ? `${inputRect.bottom - offsetTop + 6}px`
            : `${inputRect.top - offsetTop - options.clientHeight - 6}px`;
    },
    canSelectMore() {
        return !this.max || !this.items || this.items.length < this.max;
    },
    selectOption(id) {
        this.selected = id;
    },
    pickOption(id) {
        this.$refs.searchInput.focus({
            preventScroll: true,
        });

        if (this.isChecked(id)) {
            this.removeOption(id);
            return;
        }

        if (!this.canSelectMore() || !id) {
            return;
        }

        if (!this.items) {
            this.items = [id]
            return;
        }

        this.items.push(id);
    },
    removeOption(id) {
        this.items = this.items?.filter((item) => item !== id);
    },
    isChecked(id) {
        return this.items?.find((item) => item === id);
    },
    setSelectedOption(direction) {
        if (!this.isOpen) {
            this.isOpen = true
        }

        const options = this.filteredOnlyOptions;

        if (!options.length) {
            return;
        }

        const index = options.findIndex(option => option.id == this.selected);

        const option     = direction === 'up' ? options[options.length - 1] : options[0];
        const nextOption = direction === 'up' ? options[index - 1] : options[index + 1];

        this.selectOption(nextOption === undefined ? option.id : nextOption.id);

        this.scrollOption(direction, nextOption);
    },
    scrollOption(direction, nextOption) {
        const index = this.filteredOnlyOptions.find(option => option.id == this.selected).index;

        const container = this.$refs.containerOptions.getBoundingClientRect();

        const option = this.$refs.containerOptions.querySelector('.option-' + index).getBoundingClientRect();

        const optionIsVisible =  option.bottom > container.top && option.top < container.bottom;

        if (optionIsVisible) {
            return;
        }

        if (nextOption === undefined) {
            this.$refs.containerOptions.scrollTo({
                top: direction === 'down' ? 0 : this.$refs.containerOptions.scrollHeight,
            });
            return;
        }

        const isBottom = (option.bottom - option.height) > container.bottom;
        const isTop = (option.top + option.height) < container.top;

        const scrollVal = (isBottom || isTop) ? option.height * 2 : option.height;

        this.$refs.containerOptions.scrollBy({
            top: direction === 'down' ? scrollVal : -scrollVal,
        });
    }
})
